import {
  HEADER_FILL,
  NA_FILL,
  TITLE_FONT,
  VALUE_FILL,
  hideRowsThrough,
} from "./detailSheetCommon.js";
import {
  COL_D,
  COL_E,
  COL_F,
  COL_G,
  COL_H,
  COL_I,
  COL_J,
  formatNumber,
  formatPercent,
  getFyLabels,
  parseBorderSpec,
  safeText,
  setCell,
  setGrey,
  toYesNo,
  toYesNoNa,
} from "./principle6SheetHelpers.js";

const PRINCIPLE_TITLE =
  "PRINCIPLE 9 Businesses should engage with and provide value to their consumers in a responsible manner";

const SECTION_FONT = { name: "Calibri", size: 12, bold: true };

const COMPLAINT_CATEGORIES = [
  ["Data privacy", "dataPrivacy"],
  ["Advertising", "advertising"],
  ["Cyber-security", "cyberSecurity"],
  ["Delivery of essential services", "deliveryEssentialServices"],
  ["Restrictive Trade Practices", "restrictiveTradePractices"],
  ["Unfair Trade Practices", "unfairTradePractices"],
  ["Other", "other"],
];

const TURNOVER_ROWS = [
  [
    "Environmental and social parameters relevant to the product",
    "environmentalAndSocialParameters",
  ],
  ["Safe and responsible usage", "safeAndResponsibleUsage"],
  ["Recycling and/or safe disposal", "recyclingAndSafeDisposal"],
];

const RECALL_ROWS = [
  ["Voluntary recalls", "voluntaryRecalls"],
  ["Forced recalls", "forcedRecalls"],
];

/** Exact D–J TLBR border specs from reference (top/left/bottom/right). */
const ROW_BORDERS = {
  8: ["mm--", "m---", "m---", "m---", "m---", "m---", "m--m"],
  9: ["tm--", "t---", "t---", "t---", "t---", "t---", "t--m"],
  10: ["tmtt", "tt-t", "t---", "t---", "t---", "t---", "t--m"],
  11: ["tmtt", "tttt", "----", "----", "----", "----", "---m"],
  12: ["tmtt", "tt-t", "----", "----", "----", "----", "---m"],
  13: ["tmtt", "tt-t", "----", "----", "----", "----", "---m"],
  14: ["tmtt", "tttt", "----", "----", "----", "----", "---m"],
  15: ["tmtt", "ttt-", "t-tt", "tttt", "ttt-", "t-tt", "tttm"],
  16: ["tm--", "tt-t", "tt-t", "tt-t", "tt-t", "tt-t", "tt-m"],
  17: ["tm--", "tttt", "tttt", "tttt", "tttt", "tttt", "tttm"],
  18: ["tm--", "tttt", "tttt", "tttt", "tttt", "tttt", "tttm"],
  19: ["tm--", "tttt", "tttt", "tttt", "tttt", "tttt", "tttm"],
  20: ["tm--", "tttt", "tttt", "tttt", "tttt", "tttt", "tttm"],
  21: ["tm--", "tttt", "tttt", "tttt", "tttt", "tttt", "tttm"],
  22: ["tm--", "tttt", "tttt", "tttt", "tttt", "tttt", "tttm"],
  23: ["tm--", "tttt", "tttt", "tttt", "tttt", "tttt", "tttm"],
  24: ["tm--", "-ttt", "-ttt", "----", "----", "----", "---m"],
  25: ["tm--", "tt--", "tttt", "----", "----", "----", "---m"],
  26: ["tm--", "tttt", "tttt", "----", "----", "----", "---m"],
  27: ["tmtt", "tttt", "tttt", "----", "----", "----", "---m"],
  28: ["tmtt", "----", "-t-t", "----", "----", "----", "---m"],
  29: ["tmtt", "tttt", "-ttt", "----", "----", "----", "---m"],
  30: ["tmt-", "t-t-", "t-tt", "----", "----", "----", "---m"],
  31: ["tmtt", "tttt", "tt-t", "----", "----", "----", "---m"],
  32: ["tmt-", "t-tt", "tttt", "----", "----", "----", "---m"],
  33: ["tmtt", "tttt", "tttt", "-t--", "----", "----", "---m"],
  34: ["tmt-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-tm"],
  35: ["-mtt", "-ttt", "-ttt", "----", "----", "----", "---m"],
  36: ["tmtt", "tttt", "tttt", "----", "----", "----", "---m"],
  37: ["tmtt", "tttt", "tttt", "----", "----", "----", "---m"],
  38: ["tmt-", "tttt", "tttt", "----", "----", "----", "---m"],
  39: ["tmtt", "tttt", "tttt", "----", "----", "----", "---m"],
  40: ["tmtt", "tttt", "tttt", "----", "----", "----", "---m"],
  41: ["tmmt", "ttmt", "ttmt", "--m-", "--m-", "--m-", "--mm"],
};

function setColumnWidths(ws) {
  const widths = {
    A: 9.1796875,
    B: 9.1796875,
    C: 9.1796875,
    D: 79.81640625,
    E: 27.1796875,
    F: 27.1796875,
    G: 27.1796875,
    H: 27.1796875,
    I: 27.1796875,
    J: 27.1796875,
  };
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
  ["A", "B"].forEach((col) => {
    ws.getColumn(col).hidden = true;
  });
  for (let col = 14; col <= 16384; col += 1) {
    ws.getColumn(col).hidden = true;
  }
}

function setupSheetView(workbook, ws) {
  ws.views = [
    {
      showGridLines: false,
      state: "frozen",
      xSplit: 3,
      ySplit: 8,
      topLeftCell: "D9",
      zoomScale: 80,
      zoomScaleNormal: 80,
    },
  ];
  if (!workbook.views?.length) {
    workbook.views = [{ showGridLines: false }];
  } else {
    workbook.views[0].showGridLines = false;
  }
}

function applyRowBorders(ws, row, specs) {
  specs.forEach((spec, index) => {
    ws.getCell(row, COL_D + index).border = parseBorderSpec(spec);
  });
}

function applyAllBorders(ws) {
  Object.entries(ROW_BORDERS).forEach(([row, specs]) => {
    applyRowBorders(ws, Number(row), specs);
  });
}

function writeBand(ws, row, endCol, text, specs, options = {}) {
  for (let col = COL_D; col <= endCol; col += 1) {
    setCell(ws, row, col, col === COL_D ? text : "", {
      font: options.font,
      bold: options.bold,
      align: options.align || "left",
      fill: options.fill,
      bordered: false,
      border: parseBorderSpec(specs[col - COL_D] || "----"),
    });
  }
  // Merge is applied in finalizeMergedBands after the border pass.
  if (options.height != null) ws.getRow(row).height = options.height;
}

/** Re-apply band merges after the global border pass. */
function finalizeMergedBands(ws, bands) {
  bands.forEach(({ row, endCol, align = "left" }) => {
    const specs = ROW_BORDERS[row];
    if (!specs) return;
    try {
      ws.mergeCells(row, COL_D, row, endCol);
    } catch {
      /* already merged */
    }
    const start = parseBorderSpec(specs[0]);
    const end = parseBorderSpec(specs[endCol - COL_D]);
    const master = ws.getCell(row, COL_D);
    master.border = {
      top: start.top || end.top,
      left: start.left,
      bottom: start.bottom || end.bottom,
      right: end.right || start.right,
    };
    master.alignment = {
      horizontal: align,
      vertical: "middle",
      wrapText: true,
    };
    if (endCol < COL_J) {
      specs.slice(endCol - COL_D + 1).forEach((spec, index) => {
        ws.getCell(row, endCol + 1 + index).border = parseBorderSpec(spec);
      });
    }
  });
}

function greyPad(ws, row, fromCol) {
  for (let col = fromCol; col <= COL_J; col += 1) {
    setGrey(ws, row, col, parseBorderSpec("----"));
  }
}

function addLabelValueE(ws, row, label, value, options = {}) {
  setCell(ws, row, COL_D, label, {
    align: "left",
    bordered: false,
  });
  setCell(ws, row, COL_E, value ?? "", {
    align: options.valueAlign || "left",
    fill: options.valueFill === undefined ? VALUE_FILL : options.valueFill,
    bordered: false,
  });
  greyPad(ws, row, COL_F);
  if (options.height != null) ws.getRow(row).height = options.height;
}

function addYesNoRow(ws, row, label, value, options = {}) {
  setCell(ws, row, COL_D, label, {
    align: "left",
    bordered: false,
  });
  setCell(ws, row, COL_E, value ?? "", {
    align: "center",
    fill: VALUE_FILL,
    bordered: false,
  });
  setGrey(ws, row, COL_F, parseBorderSpec("----"));
  greyPad(ws, row, COL_G);
  if (options.height != null) ws.getRow(row).height = options.height;
}

function mergeLabelDE(ws, row, label, options = {}) {
  setCell(ws, row, COL_D, label, {
    align: "left",
    bordered: false,
  });
  setCell(ws, row, COL_E, "", {
    fill: options.eFill,
    bordered: false,
  });
}

/** Re-merge D:E label rows and restore outer border after the global border pass. */
function finalizeMergedLabelRows(ws, rows) {
  rows.forEach((row) => {
    const specs = ROW_BORDERS[row];
    if (!specs) return;
    try {
      ws.mergeCells(row, COL_D, row, COL_E);
    } catch {
      /* already merged */
    }
    const d = parseBorderSpec(specs[0]);
    const e = parseBorderSpec(specs[1]);
    ws.getCell(row, COL_D).border = {
      top: d.top || e.top,
      left: d.left,
      bottom: d.bottom || e.bottom,
      right: e.right || d.right,
    };
    specs.slice(2).forEach((spec, index) => {
      ws.getCell(row, COL_F + index).border = parseBorderSpec(spec);
    });
  });
}

function finalizeComplaintHeaderMerges(ws) {
  const merges = [
    { r1: 15, c1: COL_E, r2: 15, c2: COL_F },
    { r1: 15, c1: COL_H, r2: 15, c2: COL_I },
    { r1: 15, c1: COL_G, r2: 16, c2: COL_G },
    { r1: 15, c1: COL_J, r2: 16, c2: COL_J },
  ];
  merges.forEach(({ r1, c1, r2, c2 }) => {
    try {
      ws.mergeCells(r1, c1, r2, c2);
    } catch {
      /* already merged */
    }
  });

  // Outer borders for FY / PY header merges
  const e15 = parseBorderSpec(ROW_BORDERS[15][1]);
  const f15 = parseBorderSpec(ROW_BORDERS[15][2]);
  ws.getCell(15, COL_E).border = {
    top: e15.top || f15.top,
    left: e15.left,
    bottom: e15.bottom || f15.bottom,
    right: f15.right || e15.right,
  };
  const h15 = parseBorderSpec(ROW_BORDERS[15][4]);
  const i15 = parseBorderSpec(ROW_BORDERS[15][5]);
  ws.getCell(15, COL_H).border = {
    top: h15.top || i15.top,
    left: h15.left,
    bottom: h15.bottom || i15.bottom,
    right: i15.right || h15.right,
  };

  // Remark columns span two rows — use outer border + centered text
  const gTop = parseBorderSpec(ROW_BORDERS[15][3]);
  const gBot = parseBorderSpec(ROW_BORDERS[16][3]);
  const remarkG = ws.getCell(15, COL_G);
  remarkG.value = "Remark";
  remarkG.border = {
    top: gTop.top,
    left: gTop.left || gBot.left,
    bottom: gBot.bottom || gTop.bottom,
    right: gTop.right || gBot.right,
  };
  remarkG.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  remarkG.fill = NA_FILL;
  remarkG.font = { ...remarkG.font, bold: true };

  const jTop = parseBorderSpec(ROW_BORDERS[15][6]);
  const jBot = parseBorderSpec(ROW_BORDERS[16][6]);
  const remarkJ = ws.getCell(15, COL_J);
  remarkJ.value = "Remark";
  remarkJ.border = {
    top: jTop.top,
    left: jTop.left || jBot.left,
    bottom: jBot.bottom || jTop.bottom,
    right: jTop.right || jBot.right,
  };
  remarkJ.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  remarkJ.fill = NA_FILL;
  remarkJ.font = { ...remarkJ.font, bold: true };

  const fy = ws.getCell(15, COL_E);
  fy.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  const py = ws.getCell(15, COL_H);
  py.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
}

function addMergedLabelValueF(ws, row, label, value, options = {}) {
  mergeLabelDE(ws, row, label, options);
  setCell(ws, row, COL_F, value ?? "", {
    align: options.valueAlign || "left",
    fill: options.valueFill,
    bordered: false,
  });
  greyPad(ws, row, COL_G);
  if (options.height != null) ws.getRow(row).height = options.height;
}

function formatChannels(channels) {
  const list = Array.isArray(channels) ? channels : [];
  return list
    .map((item) => {
      const name = safeText(item?.chennel ?? item?.channel, "");
      const link = safeText(item?.link, "");
      if (!name && !link) return "";
      if (name && link) return `${name} - ${link}`;
      return name || link;
    })
    .filter(Boolean)
    .join("\n");
}

export function buildPrinciple9Sheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet("PRINCIPLE 9");
  setColumnWidths(ws);
  setupSheetView(workbook, ws);
  hideRowsThrough(ws, 5);

  const principle = sectionC?.principle9 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const { current: fyCurrent, previous: fyPrevious } = getFyLabels(reportingYear);
  const turnover = essential?.turnoverPercentageWithInformation || {};
  const complaints = essential?.consumerComplaints || {};
  const currComplaints = complaints?.currentFinancialYear || {};
  const prevComplaints = complaints?.previousFinancialYear || {};
  const recalls = essential?.productRecalls || {};
  const cyber = essential?.cyberSecurityAndDataPrivacyPolicy || {};
  const breaches = essential?.dataBreachDetails || {};
  const productInfo = leadership?.productInformationDisplayed || {};
  const survey = leadership?.consumerSatisfactionSurvey || {};
  const cyberAnswer = toYesNo(cyber?.action);
  const productAnswer = toYesNoNa(productInfo?.action);
  const surveyAnswer = toYesNo(survey?.action);

  writeBand(ws, 8, COL_J, PRINCIPLE_TITLE, ROW_BORDERS[8], {
    font: TITLE_FONT,
    fill: HEADER_FILL,
    align: "center",
    height: 30,
  });

  writeBand(ws, 9, COL_J, "Essential Indicators", ROW_BORDERS[9], {
    font: SECTION_FONT,
    fill: NA_FILL,
    align: "center",
    height: 20.15,
  });

  addLabelValueE(
    ws,
    10,
    "1. Describe the mechanisms in place to receive and respond to consumer complaints and feedback.",
    safeText(essential?.consumerComplaintsMechanisms, ""),
    { height: 30 }
  );

  setCell(
    ws,
    11,
    COL_D,
    "2. Turnover of products and/ services as a percentage of turnover from all products/service that carry information about",
    { align: "left", bordered: false }
  );
  setCell(ws, 11, COL_E, "As a percentage to total turnover", {
    bold: true,
    align: "center",
    vertical: "middle",
    fill: NA_FILL,
    bordered: false,
  });
  greyPad(ws, 11, COL_F);
  ws.getRow(11).height = 29;

  TURNOVER_ROWS.forEach(([label, key], idx) => {
    const row = 12 + idx;
    setCell(ws, row, COL_D, label, { align: "left", bordered: false });
    setCell(ws, row, COL_E, formatPercent(turnover?.[key]), {
      align: "center",
      fill: VALUE_FILL,
      bordered: false,
    });
    greyPad(ws, row, COL_F);
    ws.getRow(row).height = 20.15;
  });

  setCell(ws, 15, COL_D, "3. Number of consumer complaints in respect of the following", {
    align: "left",
    bordered: false,
  });
  setCell(ws, 15, COL_E, fyCurrent, {
    bold: true,
    align: "center",
    vertical: "middle",
    fill: NA_FILL,
    bordered: false,
  });
  setCell(ws, 15, COL_F, "", { fill: NA_FILL, bordered: false });
  setCell(ws, 15, COL_G, "Remark", {
    bold: true,
    align: "center",
    vertical: "middle",
    fill: NA_FILL,
    bordered: false,
  });
  setCell(ws, 15, COL_H, fyPrevious, {
    bold: true,
    align: "center",
    vertical: "middle",
    fill: NA_FILL,
    bordered: false,
  });
  setCell(ws, 15, COL_I, "", { fill: NA_FILL, bordered: false });
  setCell(ws, 15, COL_J, "Remark", {
    bold: true,
    align: "center",
    vertical: "middle",
    fill: NA_FILL,
    bordered: false,
  });

  setCell(ws, 16, COL_D, "", { bordered: false });
  [
    [COL_E, "Received during the year"],
    [COL_F, "Pending resolution at end of year"],
    [COL_H, "Received during the year"],
    [COL_I, "Pending resolution at end of year"],
  ].forEach(([col, label]) => {
    setCell(ws, 16, col, label, {
      bold: true,
      align: "center",
      vertical: "middle",
      fill: NA_FILL,
      bordered: false,
    });
  });
  setCell(ws, 16, COL_G, "", { fill: NA_FILL, bordered: false });
  setCell(ws, 16, COL_J, "", { fill: NA_FILL, bordered: false });
  ws.getRow(15).height = 20.15;
  ws.getRow(16).height = 45;

  COMPLAINT_CATEGORIES.forEach(([label, key], idx) => {
    const row = 17 + idx;
    const curr = currComplaints?.[key] || {};
    const prev = prevComplaints?.[key] || {};
    setCell(ws, row, COL_D, label, { align: "left", bordered: false });
    setCell(ws, row, COL_E, formatNumber(curr?.received), {
      align: "center",
      fill: VALUE_FILL,
      bordered: false,
    });
    setCell(ws, row, COL_F, formatNumber(curr?.pendings), {
      align: "center",
      fill: VALUE_FILL,
      bordered: false,
    });
    setCell(ws, row, COL_G, safeText(curr?.remark, ""), {
      align: "left",
      fill: VALUE_FILL,
      bordered: false,
    });
    setCell(ws, row, COL_H, formatNumber(prev?.received), {
      align: "center",
      fill: VALUE_FILL,
      bordered: false,
    });
    setCell(ws, row, COL_I, formatNumber(prev?.pendings), {
      align: "center",
      fill: VALUE_FILL,
      bordered: false,
    });
    setCell(ws, row, COL_J, safeText(prev?.remark, ""), {
      align: "left",
      fill: VALUE_FILL,
      bordered: false,
    });
    ws.getRow(row).height = 20.15;
  });

  setCell(ws, 24, COL_D, "4. Details of instances of product recalls on account of safety issues", {
    align: "left",
    bordered: false,
  });
  setCell(ws, 24, COL_E, "Number", {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
  });
  setCell(ws, 24, COL_F, "Reasons for recall", {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
  });
  greyPad(ws, 24, COL_G);

  RECALL_ROWS.forEach(([label, key], idx) => {
    const row = 25 + idx;
    const item = recalls?.[key] || {};
    setCell(ws, row, COL_D, label, { align: "left", bordered: false });
    setCell(ws, row, COL_E, formatNumber(item?.number), {
      align: "center",
      fill: VALUE_FILL,
      bordered: false,
    });
    setCell(ws, row, COL_F, safeText(item?.reasonForRecall, ""), {
      align: "left",
      fill: VALUE_FILL,
      bordered: false,
    });
    greyPad(ws, row, COL_G);
    ws.getRow(row).height = 20.15;
  });

  addYesNoRow(
    ws,
    27,
    "5. Does the entity have a framework/ policy on cyber security and risks related to data privacy?",
    cyberAnswer,
    { height: 30 }
  );

  addLabelValueE(
    ws,
    28,
    "If available, provide a web-link of the policy",
    cyberAnswer === "Yes" ? safeText(cyber?.webLink, "") : ""
  );

  addLabelValueE(
    ws,
    29,
    "6. Provide details of any corrective actions taken or underway on issues relating to advertising, and delivery of essential services; cyber security and data privacy of customers; re-occurrence of instances of product recalls; penalty / action taken by regulatory authorities on safety of products / services.",
    safeText(essential?.correctiveActions, "")
  );

  writeBand(
    ws,
    30,
    COL_F,
    "7. Provide the following information relating to data breaches:",
    ROW_BORDERS[30].slice(0, 3),
    { height: 20.15 }
  );
  greyPad(ws, 30, COL_G);

  addMergedLabelValueF(
    ws,
    31,
    "a. Number of instances of data breaches along-with impact",
    formatNumber(breaches?.instancesOfDataBreaches),
    { valueFill: VALUE_FILL, valueAlign: "center" }
  );
  addMergedLabelValueF(
    ws,
    32,
    "b. Percentage of data breaches involving personally identifiable information of customers",
    formatPercent(breaches?.percentageInvolvingPersonalData),
    { valueFill: VALUE_FILL, valueAlign: "center" }
  );
  addMergedLabelValueF(
    ws,
    33,
    "c. Impact, if any, of the data breaches",
    safeText(breaches?.impactOfDataBreaches, ""),
    { valueFill: VALUE_FILL }
  );

  writeBand(ws, 34, COL_J, "Leadership Indicators", ROW_BORDERS[34], {
    font: SECTION_FONT,
    fill: NA_FILL,
    align: "center",
    height: 20.15,
  });

  addMergedLabelValueF(
    ws,
    35,
    "1. Channels / platforms where information on products and services of the entity can be accessed (provide web link, if available).",
    formatChannels(leadership?.channelsForProductInformation)
  );
  addMergedLabelValueF(
    ws,
    36,
    "2. Steps taken to inform and educate consumers about safe and responsible usage of products and/or services",
    safeText(leadership?.consumerEducation, "")
  );
  addMergedLabelValueF(
    ws,
    37,
    "3. Mechanisms in place to inform consumers of any risk of disruption/discontinuation of essential services.",
    safeText(leadership?.riskDisclosureMechanisms, "")
  );

  addYesNoRow(
    ws,
    38,
    "4. Does the entity display product information on the product over and above what is mandated as per local laws?",
    productAnswer
  );

  addLabelValueE(
    ws,
    39,
    "If yes, provide details in brief.",
    productAnswer === "Yes" ? safeText(productInfo?.remark, "") : ""
  );

  addYesNoRow(
    ws,
    40,
    "Did your entity carry out any survey with regard to consumer satisfaction relating to the major products / services of the entity, significant locations of operation of the entity or the entity as a whole?",
    surveyAnswer
  );

  mergeLabelDE(ws, 41, "Notes");
  setCell(ws, 41, COL_F, safeText(principle?.notes || leadership?.notes || essential?.notes, ""), {
    align: "left",
    bordered: false,
  });
  greyPad(ws, 41, COL_G);
  ws.getRow(41).height = 30;

  // Final pass: exact reference borders, then restore merges.
  applyAllBorders(ws);
  finalizeMergedBands(ws, [
    { row: 8, endCol: COL_J, align: "center" },
    { row: 9, endCol: COL_J, align: "center" },
    { row: 30, endCol: COL_F, align: "left" },
    { row: 34, endCol: COL_J, align: "center" },
  ]);
  finalizeComplaintHeaderMerges(ws);
  finalizeMergedLabelRows(ws, [31, 32, 33, 35, 36, 37, 41]);

  // Keep turnover heading centered after border pass.
  const turnoverHeading = ws.getCell(11, COL_E);
  turnoverHeading.alignment = { horizontal: "center", vertical: "middle", wrapText: true };

  return ws;
}
